/**
 * apps/server/client — Console : gestion des affichages
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * Liste les écrans auto-enregistrés (§5.bis). Pour chacun : SON on/off (indépendant,
 * activable sur autant d'écrans qu'on veut) et FILTRE DE SERVICES. Boutons globaux
 * « activer / couper le son partout ». Toute modification est POUSSÉE en direct à
 * l'écran (WS `displayConfig`). Rafraîchit sur `displaysChanged`.
 */

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useSocket } from '../../contexts/SocketContext';
import { useConfig } from '../../contexts/ConfigContext';

export default function DisplaysManager() {
  const { socket } = useSocket();
  const { config } = useConfig();
  const services = config?.serviceTypes || [];

  const [displays, setDisplays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get('/api/displays');
      const list = res.data?.data ?? res.data ?? [];
      setDisplays(Array.isArray(list) ? list : []);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!socket) return;
    const onChange = () => load();
    socket.on('displaysChanged', onChange);
    return () => socket.off('displaysChanged', onChange);
  }, [socket, load]);

  const update = async (displayId, patch) => {
    try { await axios.put(`/api/displays/${encodeURIComponent(displayId)}`, patch); await load(); }
    catch (e) { setError(e.response?.data?.error || e.message); }
  };

  const soundAll = async (on) => {
    try { await axios.post('/api/displays/sound-all', { sound: on }); await load(); }
    catch (e) { setError(e.response?.data?.error || e.message); }
  };

  const toggleService = (d, serviceId) => {
    const cur = Array.isArray(d.services) ? d.services : [];
    const next = cur.includes(serviceId) ? cur.filter((s) => s !== serviceId) : [...cur, serviceId];
    update(d.displayId, { services: next });
  };

  const remove = async (displayId) => {
    if (!window.confirm('Oublier cet affichage ?')) return;
    try { await axios.delete(`/api/displays/${encodeURIComponent(displayId)}`); await load(); }
    catch (e) { setError(e.response?.data?.error || e.message); }
  };

  // Test audio CIBLÉ sur un écran : le son sort là où est l'écran. Ignore le réglage
  // `sound`/beep du moment — on éprouve la chaîne audio, pas la config. `null` le temps
  // de l'aller-retour pour désactiver le bouton et montrer que ça part.
  const [testing, setTesting] = useState(null);
  const test = async (displayId, kind) => {
    setTesting(`${displayId}:${kind}`);
    setError(null);
    try { await axios.post(`/api/displays/${encodeURIComponent(displayId)}/test`, { kind }); }
    catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setTesting(null); }
  };

  const fmtSeen = (t) => (t ? new Date(t).toLocaleTimeString() : '—');

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h1 className="text-2xl font-bold text-gray-800">Gestion des affichages</h1>
          <div className="flex items-center gap-2">
            <button onClick={() => soundAll(true)} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">🔊 Son partout</button>
            <button onClick={() => soundAll(false)} className="px-3 py-1.5 text-sm bg-gray-700 text-white rounded hover:bg-gray-800">🔇 Couper partout</button>
            <button onClick={load} className="px-3 py-1.5 text-sm bg-white border rounded hover:bg-gray-100">↻</button>
          </div>
        </div>
        <p className="text-sm text-gray-500 mb-6">
          <strong>Approbation</strong> (statut en lecture seule) : gérée par l'éditeur depuis la <strong>console centrale</strong> —
          un écran « en attente » n'affiche <strong>rien</strong> tant qu'il n'est pas approuvé. Ici, vous réglez le
          <strong> son</strong> (par écran, activable sur plusieurs ; nouvel écran <strong>muet</strong>) et le
          <strong> filtre de services</strong> (aucun coché = <em>tous</em>).
        </p>

        {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">{error}</div>}
        {loading ? (
          <div className="text-gray-500">Chargement…</div>
        ) : displays.length === 0 ? (
          <div className="p-6 bg-white border rounded text-gray-500 text-center">
            Aucun affichage connecté. Ouvrez <code>/display</code> sur un écran/téléphone du réseau ;
            il apparaîtra ici automatiquement.
          </div>
        ) : (
          <div className="space-y-4">
            {displays.map((d) => (
              <div key={d.displayId} className="bg-white border rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className={`inline-block w-2.5 h-2.5 rounded-full ${d.online ? 'bg-green-500' : 'bg-gray-300'}`}
                      title={d.online ? 'En ligne' : 'Hors ligne'}
                    />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <input
                          defaultValue={d.name}
                          onBlur={(e) => { if (e.target.value !== d.name) update(d.displayId, { name: e.target.value }); }}
                          className="font-semibold text-gray-800 border-b border-transparent hover:border-gray-300 focus:border-blue-400 outline-none bg-transparent"
                        />
                        {/* Libellé master/slave = dérivé du son : master = annonce (son ON), slave = muet. */}
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${d.sound ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                          {d.sound ? '🔊 master' : '🔇 slave'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-400 truncate">
                        {d.displayId} · {d.online ? 'en ligne' : `vu ${fmtSeen(d.lastSeen)}`}
                        {/* Voix ÉNUMÉRÉES par l'écran. `0` n'est PAS un défaut : sur Android,
                            le moteur TTS synthétise avec sa voix par défaut sans forcément
                            exposer `getVoices()`. Le vrai test de santé, c'est le bouton 🗣. */}
                        {d.voices && (
                          <span
                            className="ml-2 text-gray-400"
                            title={
                              d.voices.langs?.length
                                ? `Langues énumérées : ${d.voices.langs.join(', ')}`
                                : "Aucune voix énumérée — l'écran peut tout de même parler avec la voix par défaut du moteur (fréquent sur Android). Utilisez 🗣 Voix pour vérifier."
                            }
                          >
                            🎙 {d.voices.count} voix{d.voices.langs?.length ? ` (${d.voices.langs.join(', ')})` : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Approbation = LECTURE SEULE ici : elle est pilotée par la console centrale (éditeur). */}
                    <span
                      className={`px-3 py-1.5 text-sm rounded border font-medium ${
                        d.enabled ? 'bg-green-50 text-green-700 border-green-300' : 'bg-amber-50 text-amber-700 border-amber-300'
                      }`}
                      title="Approbation gérée depuis la console centrale (éditeur)"
                    >
                      {d.enabled ? '✅ Approuvé' : '⛔ En attente'}
                    </span>
                    <button
                      onClick={() => update(d.displayId, { sound: !d.sound })}
                      className={`px-3 py-1.5 text-sm rounded border ${
                        d.sound ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 hover:bg-gray-100'
                      }`}
                      title={d.sound ? 'Son activé — cliquer pour couper' : 'Muet — cliquer pour activer'}
                    >
                      {d.sound ? '🔊 Son activé' : '🔇 Muet'}
                    </button>
                    {/* Tests audio ciblés : jouent SUR cet écran, quel que soit son réglage son. */}
                    <button
                      onClick={() => test(d.displayId, 'beep')}
                      disabled={!d.online || testing === `${d.displayId}:beep`}
                      className="px-3 py-1.5 text-sm rounded border bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      title={d.online ? 'Jouer un bip sur cet écran' : 'Écran hors ligne'}
                    >
                      {testing === `${d.displayId}:beep` ? '…' : '🔔 Bip'}
                    </button>
                    <button
                      onClick={() => test(d.displayId, 'voice')}
                      disabled={!d.online || testing === `${d.displayId}:voice`}
                      className="px-3 py-1.5 text-sm rounded border bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      title={d.online ? 'Faire parler cet écran' : 'Écran hors ligne'}
                    >
                      {testing === `${d.displayId}:voice` ? '…' : '🗣 Voix'}
                    </button>
                    <button onClick={() => remove(d.displayId)} className="px-2 py-1.5 text-sm text-red-500 hover:bg-red-50 rounded" title="Oublier">🗑</button>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-2 items-center">
                  {services.length === 0 && <span className="text-xs text-gray-400">Aucun service configuré.</span>}
                  {services.map((s) => {
                    const on = Array.isArray(d.services) && d.services.includes(s.id);
                    return (
                      <button
                        key={s.id}
                        onClick={() => toggleService(d, s.id)}
                        className={`px-2.5 py-1 text-xs rounded-full border ${
                          on ? 'bg-blue-50 text-blue-700 border-blue-300' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        {on ? '✓ ' : ''}{s.name || s.id}
                      </button>
                    );
                  })}
                  {(!d.services || d.services.length === 0) && services.length > 0 && (
                    <span className="text-xs text-gray-400 ml-1">(tous les services)</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
